import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Battery,
  Check,
  ExternalLink,
  Gauge,
  Shield,
  Weight,
  Wrench
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { useSEO } from '../hooks/useSEO';
import {
  buildProductPath,
  getProductById,
  getProductByRoute,
  getProductLineById,
  getProductsPath
} from '../data/productsLoader';
import {
  STORE_ACCESSORIES_URL,
  STORE_PARTS_URL,
  getAccessoryCategoryById,
  getPartCategoryById
} from '../data/storeCatalog';

const SPEC_LABELS: Record<string, string> = {
  range: 'Autonomia',
  maxSpeed: 'Velocidad maxima',
  motor: 'Potencia',
  motorMax: 'Potencia maxima',
  weight: 'Peso neto',
  battery: 'Bateria (HPB)',
  wheels: 'Tipo y tamano de rueda',
  frontBrake: 'Freno delantero',
  rearBrake: 'Freno trasero',
  brake: 'Freno',
  frontSuspension: 'Suspension delantera',
  rearSuspension: 'Suspension trasera',
  suspension: 'Suspension',
  traction: 'Traccion',
  maxLoad: 'Capacidad de carga',
  chargingTime: 'Tiempo de carga',
  maxSlope: 'Pendiente maxima',
  motorType: 'Motor',
  speedModes: 'Rangos de velocidad',
  display: 'Pantalla',
  ipRating: 'Grado de proteccion IP',
  dimensions: 'Dimension neta',
  frameMaterial: 'Material del chasis',
  reflectors: 'Reflectores',
  lights: 'Luces',
  throttle: 'Acelerador',
  mast: 'Mastil',
  deck: 'Alfombrilla',
  recommendedAge: 'Edad recomendada',
  ce: 'CE'
};

function getSpecLabel(label: string) {
  return SPEC_LABELS[label] ?? label;
}

export default function ProductDetail() {
  const { lineId, productId, id } = useParams<{
    lineId?: string;
    productId?: string;
    id?: string;
  }>();
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState(0);

  const product = useMemo(() => {
    if (lineId && productId) {
      return getProductByRoute(lineId, productId);
    }

    if (id) {
      return getProductById(id);
    }

    return undefined;
  }, [id, lineId, productId]);

  const productLine = product ? getProductLineById(product.lineId) : undefined;
  const canonicalPath = product ? buildProductPath(product.lineId, product.id) : getProductsPath();

  useEffect(() => {
    setSelectedImage(0);
  }, [product?.id]);

  useSEO({
    title: product ? `${product.name} | Patinete electrico Zwheel` : 'Patinetes electricos Zwheel',
    description: product
      ? `${product.name}. ${product.slogan}. Descubre sus especificaciones, puntos fuertes y acceso directo a la tienda oficial.`
      : 'Explora la gama de patinetes electricos Zwheel.',
    keywords: product
      ? `${product.name}, ${product.lineName}, patinete electrico, Zwheel`
      : 'patinetes electricos, Zwheel'
  });

  if (product && location.pathname !== canonicalPath) {
    return <Navigate to={canonicalPath} replace />;
  }

  if (!product || !productLine) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-lg">
          <h1 className="text-3xl font-bold text-zwheel-black mb-4">Modelo no encontrado</h1>
          <p className="text-zwheel-gray-600 mb-8">
            No hemos encontrado el patinete que buscas. Puedes volver a la gama completa y seguir explorando.
          </p>
          <Button asChild>
            <Link to={getProductsPath()}>Ver patinetes electricos</Link>
          </Button>
        </div>
      </div>
    );
  }

  const accessoryCategories = product.accessoryCategoryIds
    .map((categoryId) => getAccessoryCategoryById(categoryId))
    .filter(Boolean);

  const partCategories = product.partCategoryIds
    .map((categoryId) => getPartCategoryById(categoryId))
    .filter(Boolean);
  const specEntries = Object.entries(product.specs).filter(([, value]) => value);

  const primaryMetrics = [
    {
      label: 'Autonomia',
      value: product.specs.range,
      icon: Battery
    },
    {
      label: 'Potencia',
      value: product.specs.motor,
      icon: Gauge
    },
    {
      label: 'Peso',
      value: product.specs.weight,
      icon: Weight
    }
  ];

  return (
    <div className="bg-white">
      <section className="pt-28 pb-12 bg-gradient-to-b from-gray-50 via-white to-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-8">
            <Link to={getProductsPath()} className="inline-flex items-center hover:text-black transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a patinetes
            </Link>
            <span>/</span>
            <Link to={`${getProductsPath()}#${product.lineId}`} className="hover:text-black transition-colors">
              {product.lineName}
            </Link>
            <span>/</span>
            <span className="text-black">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 items-start">
            <AnimatedSection>
              <div className="space-y-4">
                <div className="rounded-3xl bg-gray-50 border border-gray-100 overflow-hidden aspect-square">
                  <img
                    src={product.images?.[selectedImage] || product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-6"
                  />
                </div>

                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {product.images.map((image, index) => (
                      <button
                        key={image}
                        type="button"
                        onClick={() => setSelectedImage(index)}
                        className={`rounded-2xl overflow-hidden border transition-all ${
                          selectedImage === index
                            ? 'border-black shadow-md'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full aspect-square object-cover bg-gray-50"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="space-y-8">
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full bg-black text-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]">
                    Serie {product.lineName}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]">
                    Oficial Zwheel
                  </span>
                </div>

                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4">
                    {product.name}
                  </h1>
                  <p className="text-xl text-gray-600 leading-relaxed mb-5">
                    {product.technicalCallout}
                  </p>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {productLine.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {primaryMetrics.map((metric) => (
                    <div key={metric.label} className="rounded-2xl border border-gray-200 bg-white p-4">
                      <metric.icon className="w-5 h-5 text-primary mb-3" />
                      <div className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</div>
                      <div className="text-xs uppercase tracking-[0.12em] text-gray-500">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {product.colors && product.colors.length > 0 && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-5">
                    <div className="text-xs uppercase tracking-[0.12em] text-gray-500 mb-3">Escoge tu color</div>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((color) => (
                        <span
                          key={color}
                          className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-black hover:bg-gray-900 text-white">
                    <a href={product.shopUrl} target="_blank" rel="noopener noreferrer">
                      Comprar en la tienda
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/accesorios-recambios">
                      Accesorios y recambios
                    </Link>
                  </Button>
                </div>

                <div className="rounded-2xl bg-gray-50 border border-gray-100 p-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h2 className="font-semibold text-gray-900 mb-2">Compra guiada desde el sitio oficial</h2>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Esta pagina te ayuda a entender el modelo, comparar sus puntos fuertes y acceder a la tienda
                        oficial para consultar precio, stock y opciones de compra.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-3xl mb-10">
              <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
                Lo esencial
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Por que elegir {product.name}
              </h2>
              <p className="text-lg text-gray-600">
                Una lectura rapida de sus ventajas para entender si encaja con tu forma de moverte.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {product.highlights.slice(0, 9).map((highlight) => (
                <div key={highlight} className="rounded-2xl border border-gray-200 bg-white p-5">
                  <Check className="w-5 h-5 text-primary mb-3" />
                  <p className="text-gray-700 leading-relaxed">{highlight}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="grid grid-cols-1 lg:grid-cols-[0.7fr_1.3fr] gap-8 items-start">
              <div>
                <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
                  Para quien es
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Pensado para un uso real
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  Esta serie esta diseñada para usuarios que valoran una mezcla concreta de potencia, autonomia y
                  comportamiento segun el tipo de trayecto.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {product.useCases.map((useCase) => (
                  <div key={useCase} className="rounded-2xl bg-white border border-gray-200 p-5">
                    <div className="text-sm uppercase tracking-[0.14em] text-gray-500 mb-3">Uso recomendado</div>
                    <p className="text-lg font-semibold text-gray-900 leading-snug">{useCase}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-3xl mb-10">
              <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
                Especificaciones
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ficha tecnica
              </h2>
              <p className="text-lg text-gray-600">
                Datos clave para comparar este modelo con el resto de la gama Zwheel.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {specEntries.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between border-b border-gray-100 py-4 gap-4">
                  <span className="text-gray-500">{getSpecLabel(label)}</span>
                  <span className="text-gray-900 font-semibold text-right">{value}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-3xl mb-10">
              <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
                Compatibilidad
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Accesorios y recambios recomendados
              </h2>
              <p className="text-lg text-gray-600">
                Usa estas rutas para entrar en la tienda con una intencion mas clara y reducir errores al buscar piezas.
              </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <div className="rounded-3xl bg-white border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-bold text-gray-900">Accesorios sugeridos</h3>
                </div>
                <div className="space-y-4">
                  {accessoryCategories.map((category) => (
                    <div key={category!.id} className="rounded-2xl border border-gray-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{category!.name}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{category!.description}</p>
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                          {category!.count}
                        </span>
                      </div>
                      <div className="mt-4">
                        <Button asChild variant="outline" size="sm">
                          <a href={category!.shopUrl} target="_blank" rel="noopener noreferrer">
                            Explorar en la tienda
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5">
                  <Button asChild variant="outline">
                    <a href={STORE_ACCESSORIES_URL} target="_blank" rel="noopener noreferrer">
                      Ver todos los accesorios
                    </a>
                  </Button>
                </div>
              </div>

              <div className="rounded-3xl bg-white border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <Wrench className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-bold text-gray-900">Recambios compatibles</h3>
                </div>
                <div className="space-y-4">
                  {partCategories.map((category) => (
                    <div key={category!.id} className="rounded-2xl border border-gray-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{category!.name}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{category!.description}</p>
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                          {category!.count}
                        </span>
                      </div>
                      <div className="mt-4">
                        <Button asChild variant="outline" size="sm">
                          <a href={category!.shopUrl} target="_blank" rel="noopener noreferrer">
                            Ver categoria
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="outline">
                    <a href={product.finderShopUrl || STORE_PARTS_URL} target="_blank" rel="noopener noreferrer">
                      Buscar por modelo
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/accesorios-recambios">Ver guia de recambios</Link>
                  </Button>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="rounded-[2rem] bg-black text-white p-8 md:p-10 lg:p-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                <span className="text-sm uppercase tracking-[0.16em] text-white/60 block mb-3">
                  Siguiente paso
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Consulta precio, disponibilidad y opciones de compra
                </h2>
                <p className="text-white/70 text-lg leading-relaxed">
                  Cuando ya tengas claro el modelo, entra en la tienda oficial para revisar stock, promociones y
                  disponibilidad real.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100">
                  <a href={product.shopUrl} target="_blank" rel="noopener noreferrer">
                    Ver en la tienda
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
                  <Link to="/contacto">Hablar con un experto</Link>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
